import type React from "react";
import { Fragment, useCallback } from "react";
import { useScheduledImageSrc } from "@frontend/shared/presentation/hooks/useScheduledImageSrc";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { Badge } from "@planta-v2/pds-ui/badge";
import { StringUtils } from "@frontend/shared/presentation/utils/string.utils";
import { formatProfileSubtitle } from "@shared/utils/profile-subtitle.utils";
import type { DirectoryCard } from "@frontend/shared/domain/types/directory-card.types";
import { EventParticipantIdPublic } from "@shared/schemas/branded-id.schemas";
import { ChevronRight } from "lucide-react";
import { i18n } from "@frontend/i18n";
import { cn } from "@planta-v2/pds-ui/lib/utils";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { splitByQueryMatches } from "../../../domain/services/query-match-segments.service";
import { firstSentence } from "../../../domain/services/first-sentence.service";

const POSITION_SENTINEL = "\x00POSITION\x00";
const COMPANY_SENTINEL = "\x00COMPANY\x00";
const SUBTITLE_SENTINEL_PATTERN = new RegExp(
  `(${POSITION_SENTINEL}|${COMPANY_SENTINEL})`,
  "u",
);

interface ProfileListItemProps {
  readonly entry: DirectoryCard;
  readonly onClick: (participantIdPublic: EventParticipantIdPublic) => void;
  readonly isViewer?: boolean;
  readonly isCompact?: boolean;
  readonly markedQuery?: string | undefined;
  readonly isBioShown?: boolean;
}

function MarkedText({
  text,
  query,
}: {
  readonly text: string;
  readonly query: string;
}): React.JSX.Element {
  return (
    <>
      {splitByQueryMatches(text, query).map((segment, index) =>
        segment.isMatch ? (
          <mark
            key={index}
            className="bg-transparent font-semibold text-foreground"
          >
            {segment.text}
          </mark>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}

export function ProfileListItem({
  entry,
  onClick,
  isViewer = false,
  isCompact = false,
  markedQuery = "",
  isBioShown = false,
}: ProfileListItemProps): React.JSX.Element {
  const { displayName, company, photoUrl, participantIdPublic, headline, bio } =
    entry;
  const bioSentence =
    isBioShown && bio !== null
      ? firstSentence(LocalizedTextUtils.resolve(bio, i18n.getLocale()))
      : "";
  // The translation is filled with sentinels so role and company are marked
  // apart, and the words joining them stay the locale's and are never marked.
  const subtitleTemplate = formatProfileSubtitle(
    { title: headline, companyName: company },
    () =>
      i18n.t("shared.profile.subtitle.withCompany", {
        position: POSITION_SENTINEL,
        company: COMPANY_SENTINEL,
      }),
  );
  const subtitle = subtitleTemplate
    .split(SUBTITLE_SENTINEL_PATTERN)
    .map((part, index) => {
      const isJoiningWords =
        part !== POSITION_SENTINEL &&
        part !== COMPANY_SENTINEL &&
        part !== subtitleTemplate;
      if (isJoiningWords) return <Fragment key={index}>{part}</Fragment>;
      const text =
        part === POSITION_SENTINEL
          ? (headline ?? "")
          : part === COMPANY_SENTINEL
            ? (company ?? "")
            : part;
      return <MarkedText key={index} text={text} query={markedQuery} />;
    });
  const initials = StringUtils.getInitials(displayName);
  const { ref, scheduledSrc, onLoadingStatusChange } =
    useScheduledImageSrc(photoUrl);

  const handleClick = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement>): void => {
      onClick(participantIdPublic);
    },
    [onClick, participantIdPublic],
  );

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      className={cn(
        "flex w-full items-center gap-3 hover:bg-accent/50 transition-colors text-left border-b border-foreground/10 last:border-b-0",
        isCompact ? "py-2.5" : "py-3",
      )}
      data-testid={`directory-profile-item-${EventParticipantIdPublic.unwrap(
        participantIdPublic,
      )}`}
    >
      <Avatar className="h-10 w-10 shrink-0 rounded-full border border-foreground/10">
        {scheduledSrc !== null ? (
          <PlantaAvatarImage
            src={scheduledSrc}
            alt={displayName}
            className="object-cover"
            onLoadingStatusChange={onLoadingStatusChange}
          />
        ) : null}
        <AvatarFallback className="bg-muted-foreground/20 font-serif text-sm font-normal text-foreground/75">
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex flex-col min-w-0 flex-1 gap-px">
        <span className="font-serif text-base font-normal text-foreground leading-tight truncate">
          <MarkedText text={displayName} query={markedQuery} />
        </span>
        {subtitleTemplate !== "" && (
          <span className="text-pds-2xs text-muted-foreground leading-tight line-clamp-2">
            {subtitle}
          </span>
        )}
        {bioSentence !== "" && (
          <span
            data-testid="directory-profile-item-bio"
            className="text-pds-2xs text-muted-foreground leading-tight truncate"
          >
            {bioSentence}
          </span>
        )}
      </div>

      {isViewer && (
        <Badge
          variant="outline"
          className="shrink-0 px-2 py-0 text-pds-micro"
          data-testid="directory-profile-item-you"
        >
          {i18n.t("user.directory.list.you")}
        </Badge>
      )}
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" />
    </button>
  );
}
